import React, { useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { t } from '@okclawpro/i18n'
import { loginRemotePassword, loginRemoteSms, type RemoteUser } from './remote-auth'

type LoginMode = 'password' | 'sms'

export interface RemoteLoginPageProps {
  onLoggedIn: (user: RemoteUser) => void
}

function formatLoginError(error: unknown): string {
  const code = typeof error === 'object' && error !== null && typeof (error as { code?: unknown }).code === 'string'
    ? (error as { code: string }).code
    : error instanceof Error
      ? error.message
      : String(error)
  switch (code) {
    case 'AUTH_NOT_CONFIGURED':
      return t('webui.remote.error.AUTH_NOT_CONFIGURED')
    case 'AUTH_UNAVAILABLE':
      return t('webui.remote.error.AUTH_UNAVAILABLE')
    case 'INVALID_CREDENTIALS':
      return t('webui.remote.error.INVALID_CREDENTIALS')
    case 'SMS_CODE_INVALID':
      return t('webui.remote.error.SMS_CODE_INVALID')
    case 'SMS_CODE_EXPIRED':
      return t('webui.remote.error.SMS_CODE_EXPIRED')
    default:
      return t('webui.remote.error.loginFailed')
  }
}

export default function RemoteLoginPage({ onLoggedIn }: RemoteLoginPageProps) {
  const [mode, setMode] = useState<LoginMode>('password')
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit = mode === 'password'
    ? account.trim().length > 0 && password.length > 0
    : phone.trim().length > 0 && code.trim().length > 0

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-6 font-sans text-foreground sm:px-6">
      <section className="w-full max-w-[420px] overflow-hidden rounded-lg border border-border/70 bg-background shadow-[0_18px_60px_rgba(15,23,42,0.10)]">
        <div className="border-b border-border/70 px-5 py-5 sm:px-6">
          <div className="mb-4 inline-flex h-9 items-center justify-center rounded-md bg-zinc-950 px-3 text-[13px] font-semibold text-white dark:bg-zinc-50 dark:text-zinc-950">
            OKClaw
          </div>
          <h1 className="text-[22px] font-semibold leading-7">{t('webui.remote.login.title')}</h1>
          <p className="mt-2 text-[13px] leading-5 text-muted-foreground">{t('webui.remote.login.description')}</p>
        </div>

        <form
          className="flex flex-col gap-4 px-5 py-5 sm:px-6"
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSubmit || isSubmitting) return
            setError('')
            setIsSubmitting(true)
            const request = mode === 'password'
              ? loginRemotePassword(account.trim(), password)
              : loginRemoteSms(phone.trim(), code.trim())
            void request
              .then(onLoggedIn)
              .catch((err) => setError(formatLoginError(err)))
              .finally(() => setIsSubmitting(false))
          }}
        >
          <div className="grid h-10 grid-cols-2 rounded-md border border-border bg-muted/40 p-1">
            <button
              type="button"
              className={`rounded-[5px] text-[13px] font-medium transition-colors ${mode === 'password' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMode('password')}
            >
              {t('webui.remote.login.passwordTab')}
            </button>
            <button
              type="button"
              className={`rounded-[5px] text-[13px] font-medium transition-colors ${mode === 'sms' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMode('sms')}
            >
              {t('webui.remote.login.smsTab')}
            </button>
          </div>

          {mode === 'password' ? (
            <>
              <label className="flex flex-col gap-2 text-[13px] font-medium">
                {t('webui.remote.login.account')}
                <input
                  className="h-11 rounded-md border border-border bg-background px-3 text-[14px] outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/15"
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  autoComplete="username"
                  placeholder={t('webui.remote.login.accountPlaceholder')}
                />
              </label>
              <label className="flex flex-col gap-2 text-[13px] font-medium">
                {t('webui.remote.login.password')}
                <input
                  className="h-11 rounded-md border border-border bg-background px-3 text-[14px] outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/15"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  placeholder={t('webui.remote.login.passwordPlaceholder')}
                />
              </label>
            </>
          ) : (
            <>
              <label className="flex flex-col gap-2 text-[13px] font-medium">
                {t('webui.remote.login.phone')}
                <input
                  className="h-11 rounded-md border border-border bg-background px-3 text-[14px] outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/15"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder={t('webui.remote.login.phonePlaceholder')}
                />
              </label>
              <label className="flex flex-col gap-2 text-[13px] font-medium">
                {t('webui.remote.login.smsCode')}
                <input
                  className="h-11 rounded-md border border-border bg-background px-3 text-[14px] outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/15"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder={t('webui.remote.login.smsCodePlaceholder')}
                />
              </label>
            </>
          )}

          {error ? (
            <p className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-[13px] leading-5 text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </p>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-zinc-950 px-4 text-[14px] font-semibold text-white shadow-[0_10px_22px_rgba(15,23,42,0.18)] transition-all hover:bg-zinc-800 active:translate-y-px disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 disabled:shadow-none dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-zinc-200 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-400"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isSubmitting ? t('webui.remote.login.signingIn') : t('webui.remote.login.submit')}
          </button>
        </form>
      </section>
    </main>
  )
}
